// Label printing (handoff §7m; settings §9.9), as routes/label-printing.js
// serves it. The page and the cog read one view: the tenant's settings, every
// format it has (enabled or not, each marked with whether it is a purpose's
// default), and what the caller may change.

export type Purpose = 'item' | 'worker'
export type ContentField = 'group' | 'job' | 'variety'
export type IdMode = 'Alphanumeric' | 'Numeric'
export type Lifecycle = 'Single-use' | 'Reusable'

export interface LabelFormat {
  id: string
  name: string
  sku: string
  version: string | number
  description?: string | null
  supports_colour: boolean
  supports_purposes: string[]
  supported_optional_fields: string[]
  labels_per_sheet: number
  rows: number
  columns: number
  renderer_key: string
  enabled: boolean
  medium: string
  is_default_item: boolean
  is_default_worker: boolean
}

export interface LabelSettings {
  worker_labels: boolean
  unassigned_items: boolean
  job_assignment: boolean
  generic_items: boolean
  item_lifecycle: Lifecycle
  start_group: boolean
  start_job: boolean
  start_variety: boolean
  start_id_mode: IdMode
  default_item_sku: string
  default_worker_sku: string
  large_batch_threshold: number
  updated_at: string | null
  updated_by: string | null
}

export interface SettingsView {
  settings: LabelSettings
  formats: LabelFormat[]
  can_edit: boolean
  can_edit_capabilities: boolean
  // §7m.11 (h 4802-4806): the caller's set and what it may do here. The page
  // offers only these actions; the server checks each one again at commit.
  role?: string | null
  capabilities?: LabelCapability[]
}

export type LabelCapability =
  | 'LabelPrinting.View'
  | 'LabelPrinting.GenerateWorker'
  | 'LabelPrinting.GenerateItem'
  | 'LabelPrinting.Download'
  | 'LabelPrinting.ReprintItem'
  | 'LabelPrinting.Void'
  | 'TenantSettings.LabelPrinting'

// What the caller may do, read from the settings view. A view without the
// list (an older server) offers everything and leaves the refusal to it.
export interface LabelAccess {
  role: string | null
  generate: (p: Purpose) => boolean
  download: boolean
  reprint: (p: Purpose) => boolean
  void: boolean
}

export function labelAccess(view: SettingsView | null): LabelAccess {
  const caps = view?.capabilities
  const has = (c: LabelCapability) => !caps || caps.includes(c)
  return {
    role: view?.role ?? null,
    generate: (p) => has(p === 'worker' ? 'LabelPrinting.GenerateWorker' : 'LabelPrinting.GenerateItem'),
    download: has('LabelPrinting.Download'),
    // LABELPERM-1: a Worker ID reprint is under Download, as the server has it.
    reprint: (p) => has(p === 'worker' ? 'LabelPrinting.Download' : 'LabelPrinting.ReprintItem'),
    void: has('LabelPrinting.Void'),
  }
}

// Why an action is not offered, in the words the server refuses it with.
export const notAllowed = (role: string | null, what: string) =>
  `Your permission set${role ? ` (${role})` : ''} does not let you ${what}.`

// GET /api/label-printing/people: the active people only, by name.
export interface LabelPerson {
  id: string
  name: string | null
  group_id: string | null
  group_name: string | null
}

// GET /api/label-printing/jobs: live picking jobs, by site, variety and name.
export interface LabelJob {
  id: string
  name: string
  variety: string | null
  site_id: string
  site_name: string
}

// Personnel › Label printing hands its selection over in the history state
// (h 4602-4605), not the address: a selection can be hundreds of ids.
export interface LabelShortcut {
  labelWorkers: string[]
}

// Not configurable in v1; Black is always the start (§9.9).
export const COLOURS = [
  { name: 'Black', bg: '#2a2723', fg: '#ffffff' },
  { name: 'Red', bg: '#c2352b', fg: '#ffffff' },
  { name: 'Blue', bg: '#2f5f9e', fg: '#ffffff' },
  { name: 'Green', bg: '#3f7a4a', fg: '#ffffff' },
  { name: 'Purple', bg: '#6b4b8a', fg: '#ffffff' },
  { name: 'Yellow', bg: '#e8c02a', fg: '#231f18' },
]

export const CONTENT: { key: ContentField; title: string; note: string }[] = [
  { key: 'group', title: 'Worker group', note: 'Snapshotted into the batch as it printed, not looked up again later.' },
  { key: 'job', title: 'Picking job', note: 'Printed as text only. The QR never reveals the job.' },
  { key: 'variety', title: 'Variety', note: 'Useful when one crew runs two crops off the same bin stack.' },
]

export const ID_MODE_NOTE: Record<IdMode, string> = {
  Numeric: 'Digits only. After 9999 they grow to 10000 and keep going. They never wrap and are never reused.',
  Alphanumeric: 'Digits and uppercase letters, excluding I and O. After four characters are exhausted they grow to five.',
}

export const LIFECYCLE_NOTE: Record<Lifecycle, string> = {
  'Single-use': 'A bucket finishes one product cycle and is then Used. A second scan is blocked unless an authorised user overrides it with a reason.',
  Reusable: 'A bucket returns to Available when its cycle completes and can be used again.',
}

export const plural = (n: number, one: string, many = one + 's') => `${n.toLocaleString()} ${n === 1 ? one : many}`

export const supports = (f: LabelFormat | undefined, purpose: Purpose) => !!f && (f.supports_purposes || []).includes(purpose)

// "A4 sheet · 21 per sheet · template v1" (V4's format sub-line).
export const formatLine = (f: LabelFormat) => `${f.medium} · ${f.labels_per_sheet} per sheet · template v${f.version}`

// The formats a purpose can print on, from the tenant's enabled list.
export const formatsFor = (formats: LabelFormat[], purpose: Purpose) => formats.filter((f) => f.enabled && supports(f, purpose))

// The format a purpose opens on: the tenant's default for it, else the first
// enabled format that prints it (§7m.3: no format is ever "unset").
export function startFormat(formats: LabelFormat[], purpose: Purpose): LabelFormat | undefined {
  const list = formatsFor(formats, purpose)
  return list.find((f) => (purpose === 'worker' ? f.is_default_worker : f.is_default_item)) || list[0]
}

// What a new batch starts with (§7m.3 stage 4): the tenant's defaults, kept
// to what the tenant may attach at all.
export function startContent(s: LabelSettings): ContentField[] {
  return (['group', 'job', 'variety'] as ContentField[]).filter((k) =>
    k === 'group' ? s.start_group : s.job_assignment && (k === 'job' ? s.start_job : s.start_variety),
  )
}

// The one line prefixes appear on (§7m.2).
export function qrTypeLine(purpose: Purpose, assigned: boolean) {
  if (purpose === 'worker') return 'DU. Encodes the UserId'
  return assigned ? 'DTU, Display ID and UserId' : 'DT, Display ID and TenantId'
}

export const idModeLine = (mode: IdMode) => (mode === 'Numeric' ? 'Numeric only' : 'Alphanumeric, no I or O')
